import { AnyObject, FormApi } from 'final-form';
import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import { Field } from 'react-final-form';
import { SelectItem } from 'src/models/common/SelectItem';

export type FormSelectCustomAction = {
    action: () => void;
    text: string;
};

interface IFormSelectProps {
    name: string;
    isRequired?: boolean;
    label?: string;
    fillWidth?: boolean;
    defaultOption?: string;
    className?: string;
    items: SelectItem[];
    tabIndex?: number;
    onChange?: (id: number | string) => void;
    initialValue?: number;
    beforeAction?: FormSelectCustomAction;
    form?: FormApi;
    isDisabled?: boolean;
    style?: React.CSSProperties;
}

interface IFormSelectState {
    selectListShowing: boolean;
    searchTerm: string;
    selectedItem: SelectItem;
    currentIndex: number;
    filteredList: Array<SelectItem>;
    cancelBlur: boolean;
}

export default class FormSelect extends React.Component<IFormSelectProps, IFormSelectState> {
    private inputRef = React.createRef<HTMLInputElement>();
    // private idc = false;

    constructor(props: IFormSelectProps) {
        super(props);

        this.state = {
            selectListShowing: false,
            searchTerm: '',
            selectedItem: undefined,
            currentIndex: this.props.beforeAction ? 1 : 0,
            filteredList: this.props.items,
            cancelBlur: false,
        };
    }

    keydownHandler = (e: React.KeyboardEvent<HTMLInputElement>, input: AnyObject) => {
        if (e.key === 'ArrowDown') {
            if (this.state.selectListShowing) {
                this.setState({
                    currentIndex: Math.min(
                        this.state.currentIndex + 1,
                        this.props.beforeAction ? this.state.filteredList.length : this.state.filteredList.length - 1
                    ),
                });
            } else {
                this.setState({
                    selectListShowing: true,
                    currentIndex: 0,
                    filteredList: this.props.items,
                });
            }
        } else if (e.key === 'ArrowUp') {
            this.setState({ currentIndex: Math.max(this.state.currentIndex - 1, 0) });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (this.state.filteredList.length > 0) {
                if (!this.state.selectedItem) {
                    e.stopPropagation();
                }

                if (this.props.beforeAction && this.state.currentIndex === 0) {
                    this.setState({
                        selectedItem: undefined,
                        selectListShowing: false,
                        searchTerm: '',
                    });
                    this.props.beforeAction.action();
                } else {
                    const selectItem = this.state.filteredList[this.props.beforeAction ? this.state.currentIndex - 1 : this.state.currentIndex];
                    this.setState({
                        selectedItem: selectItem,
                        selectListShowing: false,
                        searchTerm: '',
                    });
                    input.onChange(selectItem.id);
                    if (this.props.onChange) {
                        this.props.onChange(selectItem.id);
                    }
                }
            } else if (this.props.beforeAction && this.state.currentIndex === 0) {
                this.setState({
                    selectedItem: undefined,
                    selectListShowing: false,
                    searchTerm: '',
                });
                this.props.beforeAction.action();
            }
        } else if (e.key === 'Tab') {
            if (this.state.filteredList && this.state.filteredList.length > 0) {
                if (!this.state.selectedItem) {
                    if (this.state.selectListShowing) {
                        e.preventDefault();
                    }
                }

                if (this.props.beforeAction && this.state.currentIndex === 0) {
                    this.setState({
                        selectedItem: undefined,
                        selectListShowing: false,
                        searchTerm: '',
                    });
                    this.props.beforeAction.action();
                } else if (this.state.selectListShowing) {
                    const selectItem = this.state.filteredList[this.props.beforeAction ? this.state.currentIndex - 1 : this.state.currentIndex];
                    this.setState({
                        selectedItem: selectItem,
                        selectListShowing: false,
                        searchTerm: '',
                    });
                    input.onChange(selectItem.id);
                    if (this.props.onChange) {
                        this.props.onChange(selectItem.id);
                    }
                }
            } else if (this.props.beforeAction && this.state.currentIndex === 0) {
                this.setState({
                    selectedItem: undefined,
                    selectListShowing: false,
                    searchTerm: '',
                });
                this.props.beforeAction.action();
            }
            this.setState({ searchTerm: undefined, filteredList: this.props.items });
        } else if (e.key === 'Delete' && this.state.selectedItem) {
            this.setState({
                selectedItem: undefined,
                searchTerm: undefined,
                currentIndex: this.props.beforeAction ? 1 : 0,
                selectListShowing: true,
                filteredList: this.props.items,
            });
            input.onChange(undefined);
            if (this.props.onChange) {
                this.props.onChange(undefined);
            }
        } else if ((/^[a-z\d\-_\s]$/i.test(e.key) && this.state.selectedItem) || !this.state.selectListShowing) {
            if (!e.shiftKey) {
                this.setState({
                    selectListShowing: true,
                    selectedItem: undefined,
                    currentIndex: this.props.beforeAction ? 1 : 0,
                });
            }
            input.onChange(undefined);
            if (this.props.onChange) {
                this.props.onChange(undefined);
            }
        }
    };

    blur = (input: AnyObject) => {
        setTimeout(() => {
            this.setState({ selectListShowing: false, searchTerm: '', filteredList: this.props.items });
        }, 0);
    };

    hoverItem = (index: number) => {
        this.setState({ currentIndex: index });
    };

    change = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.items && this.props.items.length > 0) {
            const filteredItems = this.props.items.filter(
                (item: SelectItem) => item.name.toLowerCase().indexOf(this.inputRef.current.value.toLowerCase()) >= 0
            );
            this.setState({
                filteredList: filteredItems,
                searchTerm: e.target.value,
            });
        }
    };

    itemClick = (item: SelectItem, input: AnyObject) => {
        this.setState({ selectedItem: item, searchTerm: '', selectListShowing: false, cancelBlur: false });
        input.onChange(item.id);
        if (this.props.onChange) {
            this.props.onChange(item.id);
        }
    };

    filteredSelectList = (input: AnyObject) => {
        const beforeAction = this.props.beforeAction ? (
            <div
                className={`select-item ${this.state.currentIndex === 0 ? 'hover' : ''}`}
                onMouseEnter={() => this.hoverItem(0)}
                onClick={() => this.onActionClick(this.props.beforeAction)}
            >
                <div className="dlc-form-select-custom-action">{this.props.beforeAction.text}</div>
            </div>
        ) : undefined;
        if (!this.state.filteredList) {
            return beforeAction;
        }
        if (this.state.filteredList.length <= 0) {
            return (
                <>
                    {beforeAction}
                    <div className="lh-align-center">No Records Found</div>
                </>
            );
        }
        return (
            <>
                {beforeAction}
                {this.state.filteredList.map((item: SelectItem, index: number) => {
                    let currIndex = index;
                    if (this.props.beforeAction) {
                        currIndex = index + 1;
                    }

                    return (
                        <div
                            key={item.id}
                            className={`select-item ${this.state.currentIndex === currIndex ? 'hover' : ''}`}
                            onMouseEnter={() => this.hoverItem(currIndex)}
                            onClick={() => this.itemClick(item, input)}
                        >
                            {item.name}
                        </div>
                    );
                })}
            </>
        );
    };

    clear = (input: AnyObject) => {
        this.setState({
            searchTerm: '',
            selectedItem: undefined,
            selectListShowing: false,
            filteredList: this.props.items,
        });
        input.onChange(undefined);
        if (this.props.onChange) {
            this.props.onChange(undefined);
        }
        this.inputRef.current.focus();
    };

    showDropdown = () => {
        if (!this.state.selectListShowing) {
            this.setState({ selectListShowing: true, filteredList: this.props.items});
        } else if (this.state.selectListShowing) {
            this.setState({ selectListShowing: false, filteredList: this.props.items});
        }
    };

    mouseDownHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        this.inputRef.current.focus();
    };

    onActionClick = (action: FormSelectCustomAction) => {
        this.setState({ selectListShowing: false });
        action.action();
    };

    render() {
        return (
            <Field name={this.props.name} key={this.props.name}>
                {({ input, meta }) => {
                    const hasError = meta.submitFailed && meta.error;
                    const dropDownShowing = this.state.selectListShowing;

                    let labelClass = input.value || this.state.selectedItem ? 'reduced ' : ' ';
                    labelClass += this.props.isRequired ? 'required ' : '';
                    labelClass += hasError ? 'error' : '';
                    const inputClass = hasError ? 'error ' : '';
                    const errorHeight = hasError ? 'auto' : 0;

                    let item = undefined;
                    if (this.props.items) {
                        item = this.props.items.filter((i: SelectItem) => i.id === input.value)[0];
                    }

                    const clearEnabled = !this.props.isDisabled && (this.state.selectedItem || this.state.searchTerm || item);

                    return (
                        <div
                            className={`dlc-form-select-wrapper ${this.props.className}`}
                            style={this.props.style}
                            onMouseDown={this.mouseDownHandler}
                        >
                            <div className={`dlc-form-select ${inputClass}`}>
                                <input
                                    ref={this.inputRef}
                                    tabIndex={this.props.tabIndex}
                                    className={`dlc-form-select-input ${inputClass}`}
                                    value={this.state.searchTerm || ''}
                                    onChange={this.change}
                                    onBlur={this.blur}
                                    onClick={this.showDropdown}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => this.keydownHandler(e, input)}
                                    autoComplete="new-field-3"
                                    disabled={this.props.isDisabled}
                                />
                                <div className={`dlc-form-select-label ${labelClass}`}>{this.props.label}</div>
                                {item && <div className="dlc-form-select-selected-item">{item.name}</div>}
                                {!!clearEnabled && (
                                    <div className={`dlc-form-select-clear ${clearEnabled ? 'enabled' : ''}`} onClick={() => this.clear(input)}>
                                        <i className="fas fa-times-circle" />
                                    </div>
                                )}
                                <div className="dlc-form-select-arrow">
                                    <i className="fas fa-angle-down" />
                                </div>
                                <div className={`dlc-form-select-dropdown ${dropDownShowing ? 'showing' : 0}`}>
                                    <AnimateHeight height={this.state.selectListShowing ? 'auto' : 0}>
                                        {this.filteredSelectList(input) || <></>}
                                    </AnimateHeight>
                                </div>
                            </div>

                            <AnimateHeight height={errorHeight} duration={300}>
                                <div className="dlc-form-textbox-error">{meta.error}</div>
                            </AnimateHeight>
                        </div>
                    );
                }}
            </Field>
        );
    }

    static defaultProps = {
        fillWidth: true,
    };
}



// WEBPACK FOOTER //
// ./src/components/controls/forms/redesign/FormFilterSelect.tsx